"use client";

import { useState } from "react";

import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { toast } from "sonner";

import { updateEmailPreferenceAction } from "@/app/(user)/carteira/actions";

const frequencyLabel = (days: number) => {
  if (days === 1) return "Diário";
  if (days === 7) return "Semanal";
  if (days === 15) return "Quinzenal";
  if (days === 30) return "Mensal";
  return `A cada ${days} dias`;
};

interface EmailToggleProps {
  preferenceId: number | null;
  enabled: boolean;
  frequencyDays: number | null;
  availableFrequencies: number[];
  reportType: string;
  scopeType: string;
  scopeId: string | null;
  label: string;
  onPreferenceChange?: (next: {
    id: number | null;
    enabled: boolean;
    frequencyDays: number | null;
  }) => void;
}

export const EmailToggle = ({
  preferenceId,
  enabled,
  frequencyDays,
  availableFrequencies,
  reportType,
  scopeType,
  scopeId,
  label,
  onPreferenceChange,
}: EmailToggleProps) => {
  const [checked, setChecked] = useState(enabled);
  const [frequency, setFrequency] = useState<number | null>(frequencyDays);
  const [currentPreferenceId, setCurrentPreferenceId] = useState<number | null>(
    preferenceId,
  );

  const persist = async (
    nextEnabled: boolean,
    nextFrequency: number | null,
  ) => {
    const formData = new FormData();
    formData.set("reportType", reportType);
    formData.set("scopeType", scopeType);
    formData.set("scopeId", scopeId ?? "");
    formData.set("enabled", nextEnabled ? "1" : "0");
    if (nextFrequency !== null) {
      formData.set("frequencyDays", String(nextFrequency));
    }
    if (currentPreferenceId !== null) {
      formData.set("preferenceId", String(currentPreferenceId));
    }

    const result = await updateEmailPreferenceAction(formData);

    if (result.success) {
      const nextId =
        result.preferenceId !== null ? result.preferenceId : currentPreferenceId;
      if (result.preferenceId !== null) {
        setCurrentPreferenceId(result.preferenceId);
      }
      onPreferenceChange?.({
        id: nextId,
        enabled: nextEnabled,
        frequencyDays: nextFrequency,
      });
      return true;
    }
    return false;
  };

  const handleToggle = async (
    _event: React.ChangeEvent<HTMLInputElement>,
    next: boolean,
  ) => {
    const previous = checked;
    setChecked(next);
    const ok = await persist(next, frequency);
    if (ok) {
      toast.success("Preferência de e-mail atualizada");
      return;
    }
    setChecked(previous);
    toast.error("Não foi possível atualizar a preferência");
  };

  const handleFrequencyChange = async (next: number) => {
    const previous = frequency;
    setFrequency(next);
    const ok = await persist(checked, next);
    if (ok) {
      toast.success("Frequência atualizada");
      return;
    }
    setFrequency(previous);
    toast.error("Não foi possível atualizar a frequência");
  };

  const showFrequency = checked && frequency !== null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Switch
          checked={checked}
          onChange={handleToggle}
          title={checked ? "Ativado" : "Desativado"}
          inputProps={{ "aria-label": label }}
        />
        <Typography variant="body2">{label}</Typography>
      </Stack>
      {showFrequency ? (
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={frequency ?? ""}
            displayEmpty
            onChange={(event) =>
              handleFrequencyChange(Number(event.target.value))
            }
            inputProps={{ "aria-label": `Frequência de ${label}` }}
          >
            {availableFrequencies.map((days) => (
              <MenuItem key={days} value={days}>
                {frequencyLabel(days)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </Stack>
  );
};
